import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC] px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_campaign-leads-1/artifacts/9fqxo1et_PomenLogo_blueBG.png"
            alt="POMEN Logo"
            className="h-14 mx-auto rounded-xl mb-4"
          />
          <h1 className="text-2xl font-semibold text-gray-900">Reset Password</h1>
          <p className="text-sm text-gray-500 mt-1">Enter your email to receive a reset link</p>
        </div>

        <Card className="border border-gray-200 rounded-xl bg-white shadow-sm">
          <CardContent className="p-6">
            {sent ? (
              <div className="text-center space-y-4" data-testid="forgot-password-success">
                <div className="w-12 h-12 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-gray-900">Check your inbox</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  If an account with <span className="font-medium text-gray-900">{email}</span> exists, we've sent a password reset link to it.
                </p>
                <p className="text-xs text-gray-400">
                  The link will expire in 1 hour. Don't forget to check your spam folder.
                </p>
                <Link to="/login" className="text-sm text-[#0D0D5B] hover:underline font-medium block pt-2">Back to Sign In</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">{error}</div>}
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus className="border-gray-300 rounded-lg" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg h-11" data-testid="forgot-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send Reset Link
                </Button>
                <Link to="/login" className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> Back to Sign In
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
